import { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../state/state';
import { closeModal } from '../../../state/ModalSlice/ModalSlice';
import { RootState } from '../../../state/state';
import { collection, doc, addDoc, Timestamp, updateDoc, getDoc, arrayRemove, serverTimestamp, DocumentReference } from 'firebase/firestore';
import { db } from '../../../Config/firebase';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2';
import { toast } from 'react-toastify';

interface DelegatedRole {
    name: string;
    number: string;
    role: string;
    serviceProviderReference: DocumentReference;
    businessName: string;
    assignedSubscriptions: DocumentReference[];
    userPhoto: string;
}

interface RevokeRoleModalData {
    id?: string;
    name: string;
    number: string;
    role: string;
    isAccepted: boolean;
    businessName: string;
    actionType: 'grantRole' | 'revokeRole';
}

export default function RevokeRoleModal() {
    const dispatch = useAppDispatch();
    const isOpen = useAppSelector((state) => state.ModalSlice.RevokeRoleModalOpen);
    const modalData = useAppSelector((state: RootState) => state.ArbitrarySlice.ForRevokeRoleModal) as RevokeRoleModalData;
    const serviceProviderId = useAppSelector(selectEffectiveUID);
    const userData = useAppSelector((state) => state.userSliceV2.data);
    const [isLoading, setIsLoading] = useState(false);

    const handleClose = () => {
        dispatch(closeModal("RevokeRoleModalOpen"));
    };

    if (!modalData) {
        return null;
    }

    async function handleRevokeRole() {
        if (!serviceProviderId || !modalData.number || !modalData.role) {
            toast.error("Invalid role data");
            return;
        }

        setIsLoading(true);

        try {

            // If it's a pending invite, just update its status to Cancelled
            if (!modalData.isAccepted && modalData.id) {
                await updateDoc(
                    doc(db, "PendingActions", modalData.id),
                    {
                        status: "Cancelled",
                        updatedAt: serverTimestamp()
                    }
                );
                toast.success("Invitation cancelled successfully");
                handleClose();
                return;
            }

            // Handle accepted role revocation
            const spDocRef = doc(db, "ServiceProviders", serviceProviderId);
            const spDoc = await getDoc(spDocRef);
            
            if (!spDoc.exists()) {
                throw new Error("Service provider document not found");
            }

            const delegatedRoles = spDoc.data().delegatedRoles || [];
            const roleToRevoke = delegatedRoles.find(
                (role: DelegatedRole) => role.number === modalData.number && role.role === modalData.role
            );

            if (!roleToRevoke && modalData.isAccepted) {
                throw new Error("Role not found in delegated roles");
            }

            // 1. Remove from delegatedRoles array
            if (modalData.isAccepted && roleToRevoke) {

                await updateDoc(spDocRef, {
                    delegatedRoles: arrayRemove(roleToRevoke)
                });


                // 2. Create pending action for role revocation

                await addDoc(
                    collection(db, "PendingActions"),
                    {
                        owner: spDocRef,
                        status: "Pending",
                        type: "RoleAssignment",
                        actionType: 'revokeRole',
                        userNumber: modalData.number,
                        userName: modalData.name,
                        businessName: modalData.businessName,
                        ownerName: userData?.display_name || "",
                        roleAssigned: roleToRevoke,
                        createdAt: serverTimestamp()
                    }
                );

                toast.success("Role successfully revoked");
            }

            handleClose();
        } catch (error) {
            console.error("Error revoking role:", error);
            toast.error(modalData.isAccepted ? "Failed to revoke role" : "Failed to cancel invitation");
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Dialog open={isOpen} onClose={handleClose}>
            <DialogTitle>
                {modalData.isAccepted ? "Revoke Role" : "Cancel Invitation"}
            </DialogTitle>
            <DialogContent>
                <div className="py-4">
                    <p className="text-gray-700">
                        Are you sure you want to {modalData.isAccepted ? "revoke" : "cancel"} the {modalData.role.toLowerCase()} role
                        {modalData.isAccepted ? " from" : " invitation for"} {modalData.name}?
                    </p>
                    {modalData.isAccepted && (
                        <p className="mt-2 text-red-600 text-sm">
                            This action will remove their access to your academy immediately.
                        </p>
                    )}
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isLoading}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleRevokeRole} 
                    color="error" 
                    variant="contained"
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : modalData.isAccepted ? (
                        "Revoke Role"
                    ) : (
                        "Cancel Invitation"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
